import React from 'react'
import styled from 'styled-components'
import { useParams, Redirect } from 'react-router-dom'
import Header from '../common/header'
import Footer from '../common/footer'
import Spacer from '../lib/spacer'
import { getPostBySlug } from './posts'
import { useTranslation, useLang, LocalizedLink, localizePath } from '../lib/i18n'
import { formatDate } from './format_date'
import VersionBanner from './version-banner'
import { small } from '../lib/media_query'
import stephenAvatar from '../img/stephen.webp'

const PageWrapper = styled.div``

const HeroImage = styled.div`
    width: 100%;
    max-width: 740px;
    margin-left: auto;
    margin-right: auto;
    aspect-ratio: 1400 / 703;
    border-radius: 16px;
    overflow: hidden;
    background: #06091c;
    margin-bottom: 40px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top;
        display: block;
    }

    ${small(`
        border-radius: 8px;
        margin-bottom: 28px;
    `)}
`

const BackLink = styled(LocalizedLink)`
    font-size: 14px;
    color: ${(props) => props.theme.palette.greyScale.grey};
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    &:hover {
        color: ${(props) => props.theme.palette.brand.blue};
    }
`

const Byline = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 28px;
    padding-bottom: 28px;
    border-bottom: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};
`

const Avatar = styled.img`
    width: 44px;
    height: 44px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
`

const BylineText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
`

const AuthorName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${(props) => props.theme.palette.greyScale.darkGrey};
`

const PostDate = styled.span`
    font-size: 13px;
    color: ${(props) => props.theme.palette.greyScale.lightGrey};
`

const PostTitle = styled.h1`
    font-size: 36px;
    font-weight: 700;
    color: ${(props) => props.theme.palette.greyScale.darkGrey};
    line-height: 1.25;
    margin-bottom: 32px;

    ${small(`
        font-size: 26px;
    `)}
`

const PostContent = styled.div`
    font-size: 16px;
    line-height: 1.75;
    color: ${(props) => props.theme.palette.greyScale.grey};

    h2 {
        font-size: 22px;
        font-weight: 700;
        color: ${(props) => props.theme.palette.greyScale.darkGrey};
        margin-top: 40px;
        margin-bottom: 14px;
    }

    p {
        margin-bottom: 20px;
    }

    em {
        color: ${(props) => props.theme.palette.greyScale.grey};
    }

    strong {
        color: ${(props) => props.theme.palette.greyScale.darkGrey};
        font-weight: 600;
    }

    a {
        color: ${(props) => props.theme.palette.brand.blue};
        text-decoration: none;

        &:visited {
            color: ${(props) => props.theme.palette.brand.blue};
        }

        &:hover {
            text-decoration: underline;
        }
    }

    img.body-image {
        width: 100%;
        border-radius: 10px;
        display: block;
        margin: 32px 0;
    }
`


function renderContent(html, bodyImages) {
    let processed = html
    if (bodyImages) {
        bodyImages.forEach((src, i) => {
            processed = processed.replace(
                `<p>{{image-${i + 1}}}</p>`,
                `<img src="${src}" alt="" class="body-image" />`
            )
        })
    }
    return <PostContent dangerouslySetInnerHTML={{ __html: processed }} />
}

export default function BlogPost() {
    const { slug } = useParams()
    const { t } = useTranslation()
    const lang = useLang()
    const post = getPostBySlug(lang, slug)

    if (!post) return <Redirect to={localizePath('/blog', lang)} />

    return (
        <PageWrapper>
            <Header />
            <div className="grid-container">
                <Spacer size={48} />
                <BackLink to="/blog">&#8592; {t('blog.back')}</BackLink>
                <Spacer size={16} />
                <div className="grid-x align-center">
                    <div className="cell small-12">
                        <HeroImage>
                            {post.image
                                ? <img src={post.image} alt={post.title} />
                                : <VersionBanner version={post.version} />
                            }
                        </HeroImage>
                    </div>
                    <div className="cell small-12 large-8">
                        <Byline>
                            {post.author && <Avatar src={stephenAvatar} alt={post.author} />}
                            <BylineText>
                                {post.author && <AuthorName>{post.author}</AuthorName>}
                                <PostDate>{formatDate(post.date, t('dateLocale'))}</PostDate>
                            </BylineText>
                        </Byline>
                        <PostTitle>{post.title}</PostTitle>
                        {renderContent(post.html, post.bodyImages)}
                    </div>
                </div>
                <Spacer size={80} />
            </div>
            <Footer />
        </PageWrapper>
    )
}
